/**
 * Lightweight signal for "user has scrolled past the journey pin". Lets
 * the IframeExhibits know when to unload (src → about:blank) so their
 * inner JS / fetches / animations stop hogging the main thread once the
 * user is in the InfoSection. CameraRig writes; IframeExhibits read.
 */

type Listener = (value: boolean) => void;

let isPast = false;
const pastListeners = new Set<Listener>();

export function getJourneyPast(): boolean {
  return isPast;
}

export function setJourneyPast(next: boolean): void {
  if (next === isPast) return;
  isPast = next;
  for (const listener of pastListeners) listener(next);
}

export function subscribeJourneyPast(listener: Listener): () => void {
  pastListeners.add(listener);
  return () => {
    pastListeners.delete(listener);
  };
}

// `atEndPortal` flips on while the camera is in the last ~5% of the
// journey curve, so the in-scene EndPortal mesh can hide and the
// page-level TrailingBlob overlay can take over EARLY — both blobs share
// the shader, so swapping while the camera is parked at the portal is a
// pixel-level handoff. The point is to avoid the DOF blur the in-scene
// blob picks up from the journey's PostFX pass.
let atEnd = false;
const endListeners = new Set<Listener>();

export function getAtEndPortal(): boolean {
  return atEnd;
}

export function setAtEndPortal(next: boolean): void {
  if (next === atEnd) return;
  atEnd = next;
  for (const listener of endListeners) listener(next);
}

export function subscribeAtEndPortal(listener: Listener): () => void {
  endListeners.add(listener);
  return () => {
    endListeners.delete(listener);
  };
}

// `blobAtHandoff` is the actual "blob is parked at the centre" state. It
// stays true from when the camera reaches the EndPortal (last sliver of
// the journey, via atEndPortal) THROUGH the pin release AND while the
// user hasn't yet scrolled enough for the blob to drift toward chapter 1.
// EndPortalOrbit consumes this so the orbital ring stays visible during
// the whole parked moment, not just the journey tail.
let blobAtHandoff = false;
const handoffListeners = new Set<Listener>();

export function getBlobAtHandoff(): boolean {
  return blobAtHandoff;
}

export function setBlobAtHandoff(next: boolean): void {
  if (next === blobAtHandoff) return;
  blobAtHandoff = next;
  for (const listener of handoffListeners) listener(next);
}

export function subscribeBlobAtHandoff(listener: Listener): () => void {
  handoffListeners.add(listener);
  return () => {
    handoffListeners.delete(listener);
  };
}

// `isIntroActive` flips true while IntroLogoBreak is animating its
// approach → burst → fade sequence. ViewToggle subscribes to hide its
// "Ver Grelha" link during the explosion (clean intro moment, no UI
// chrome competing for attention). MobileGridIntro subscribes to
// fade itself in once the signal flips back to false, so the project
// register header eases into view as the explosion clears instead of
// popping into place mid-animation. Defaults to false so anything
// rendered on a non-intro route (`/grid`) reads as "intro is done".
let introActive = false;
const introListeners = new Set<Listener>();

export function getIsIntroActive(): boolean {
  return introActive;
}

export function setIsIntroActive(next: boolean): void {
  if (next === introActive) return;
  introActive = next;
  for (const listener of introListeners) listener(next);
}

export function subscribeIsIntroActive(listener: Listener): () => void {
  introListeners.add(listener);
  return () => {
    introListeners.delete(listener);
  };
}

// `trailingBlobVisible` mirrors the TrailingBlob overlay's actual opacity
// flip. EndPortal reads it every frame as a hard mutual-exclusion guard:
// whenever the dynamic overlay blob is on screen, the in-scene EndPortal
// mesh must not render — regardless of which scroll path (down, up,
// deep-link, hijack snap) produced the state. Without this the two blobs
// could coexist on upward re-entry from the info section. Polled via
// getter (no listener) because EndPortal reads it in useFrame.
let trailingBlobVisible = false;

export function getTrailingBlobVisible(): boolean {
  return trailingBlobVisible;
}

export function setTrailingBlobVisible(next: boolean): void {
  trailingBlobVisible = next;
}

// `handoffProgress` is a 0→1 scrub value driven by the HandoffPin section
// (a ScrollTrigger pin between the journey and the info section). Read each
// frame inside TrailingBlob to rotate the 3D project gallery as the user
// scrolls — turning the otherwise-static handoff moment into an exploratory
// chapter close. Polled via getter (no listener pattern) because consumers
// read it in useFrame at 60Hz.
let handoffProgress = 0;

export function getHandoffProgress(): number {
  return handoffProgress;
}

export function setHandoffProgress(next: number): void {
  handoffProgress = next;
}

// `mountProgress` is the mirror of the intro's logo break: as the user
// scrolls past the InfoSection's last chapter (the contact one), this
// climbs from 0 to 1 and drives both the blob fade-out and the W
// assembly. IntroLogoMount writes it; TrailingBlobCanvas reads it every
// frame to multiply the overlay opacity. Polled (no listener) because
// the canvas reads it inside its own RAF loop already.
let mountProgress = 0;

export function getMountProgress(): number {
  return mountProgress;
}

export function setMountProgress(next: number): void {
  mountProgress = next;
}
